import type { DxRedactionPolicies, DxStorageResetGroup } from '@kerberos-compliance/compliance-shared-ui/dx';
import { DxRedactionPolicyMap } from '@kerberos-compliance/compliance-shared-ui/dx';
import type { StorageServiceKeys } from '@/storage/types/storage.types';
import { StorageServiceKeysMap } from '@/storage/types/storage.types';

/**
 * How AML Desk's storage keys may be rendered by the shared storage panel.
 *
 * The engine - `dxRenderRedactedValue()`, `dxRedactValue()`, `dxDescribeShape()` and the
 * policy resolution that defaults to presence-only - moved to
 * `compliance-shared-ui/dx`, so this file is now only the decision, per key. It moved
 * because the second implementation had already gone wrong: Admin Desk's storage panel
 * enumerated `localStorage` and copied every raw value to the clipboard.
 *
 * Redaction is an allowlist: a value is redacted *unless* an entry below says how to
 * render it, which makes adding a storage key safe by default.
 *
 * Do not replace this with a heuristic over key spelling. `impersonationContext` holds a
 * `returnToken`, and `kycOverviewWorkItems` holds customer case data; neither name
 * contains a word a heuristic would catch.
 */
export const dxStorageRedactionPolicies: DxRedactionPolicies = {
  [StorageServiceKeysMap.OrganizationId]: DxRedactionPolicyMap.Scalar,
  [StorageServiceKeysMap.AuthSource]: DxRedactionPolicyMap.Scalar,
  [StorageServiceKeysMap.LogoutReason]: DxRedactionPolicyMap.Scalar,
  [StorageServiceKeysMap.GrantLoadingComplete]: DxRedactionPolicyMap.Scalar,
  [StorageServiceKeysMap.TimeoutWarning]: DxRedactionPolicyMap.Scalar,
  [StorageServiceKeysMap.ShowMaintenanceNotice]: DxRedactionPolicyMap.Scalar,
  [StorageServiceKeysMap.IsImpersonated]: DxRedactionPolicyMap.Scalar,
  [StorageServiceKeysMap.PendingLastVisitedUrl]: DxRedactionPolicyMap.Scalar,

  [StorageServiceKeysMap.SessionExpiryTimestamp]: DxRedactionPolicyMap.Timestamp,
  [StorageServiceKeysMap.SecurityNudgeDismissedAt]: DxRedactionPolicyMap.Timestamp,

  [StorageServiceKeysMap.OwnGrants]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.Organizations]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.WorkItemTypes]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.Contract]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.KycOverviewFilter]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.KycWorkItemList]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.KycOverviewWorkItems]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.KycCreateViewWorkItem]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.LaCreateViewState]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.DocumentLibraryFilter]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.TrainingListFilter]: DxRedactionPolicyMap.Shape,
  [StorageServiceKeysMap.WorkItemCommunicationDrafts]: DxRedactionPolicyMap.Shape,

  // Explicitly presence-only, listed so a reader sees the decision rather than the default:
  // tokens, the impersonation context (carries `returnToken`) and the profile/user objects.
  [StorageServiceKeysMap.AccessTokenKerberos]: DxRedactionPolicyMap.Presence,
  [StorageServiceKeysMap.RefreshTokenKerberos]: DxRedactionPolicyMap.Presence,
  [StorageServiceKeysMap.AccessTokenHanko]: DxRedactionPolicyMap.Presence,
  [StorageServiceKeysMap.ImpersonationContext]: DxRedactionPolicyMap.Presence,
  [StorageServiceKeysMap.UserProfile]: DxRedactionPolicyMap.Presence,
  [StorageServiceKeysMap.User]: DxRedactionPolicyMap.Presence,
};

/**
 * The storage reset the panel offers as its own button, because its consequence differs
 * in kind from clearing a cache: it logs the session out.
 */
export const dxStorageResetGroups: readonly DxStorageResetGroup[] = [
  {
    id: 'auth',
    label: 'Clear auth storage keys',
    keys: [
      StorageServiceKeysMap.AccessTokenKerberos,
      StorageServiceKeysMap.RefreshTokenKerberos,
      StorageServiceKeysMap.IsImpersonated,
      StorageServiceKeysMap.ImpersonationContext,
      StorageServiceKeysMap.SessionExpiryTimestamp,
    ],
    consequence: 'Logs this session out: clears both tokens, the impersonation context and the session expiry.',
  },
];

/**
 * The keys the panel lists even when absent, so a missing key reads as `Empty` rather
 * than as one nobody thought to look at.
 */
export const dxKnownStorageKeys: readonly StorageServiceKeys[] = Object.values(StorageServiceKeysMap);
